import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { X, Clock, Car, Wrench, CheckCircle, Circle, User, Play, Square } from 'lucide-react';
import { Booking } from '../lib/bookings';
import { useAuth } from '../hooks/useAuth';
import { useBusiness } from '../contexts/BusinessContext';
import { supabase } from '../lib/supabase';
import { useSimpleTimer } from '../contexts/SimpleTimerContext';


interface BookingQuickInfoModalProps {
  isOpen: boolean;
  onClose: () => void;
  booking: Booking | null;
}

interface WorkflowStep {
  id: string;
  name: string;
  is_completed: boolean;
  order_index: number;
  assigned_resources?: any[];
}

interface AssignedResource {
  id: string;
  first_name: string;
  last_name: string;
  avatar_url?: string;
}

export default function BookingQuickInfoModal({
  isOpen,
  onClose,
  booking
}: BookingQuickInfoModalProps) {
  const { user } = useAuth();
  const { selectedBusiness } = useBusiness();
  const { activeTimer, startTimer, stopTimer, getElapsedSeconds } = useSimpleTimer();
  const [currentResource, setCurrentResource] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [hasWorkflow, setHasWorkflow] = useState(false);
  const [workflowSteps, setWorkflowSteps] = useState<WorkflowStep[]>([]);
  const [assignedResources, setAssignedResources] = useState<AssignedResource[]>([]);
  const [isLoadingData, setIsLoadingData] = useState(true);
  const [timerLoading, setTimerLoading] = useState(false);
  const [elapsedTime, setElapsedTime] = useState(0);

  // Clear state when modal closes
  useEffect(() => {
    if (!isOpen) {
      // Reset all state when modal closes
      setCurrentResource(null);
      setAssignedResources([]);
      setWorkflowSteps([]);
      setHasWorkflow(false);
      setLoading(false);
      setIsLoadingData(true); // Reset to loading state for next open
      setElapsedTime(0);
    }
  }, [isOpen]);

  // Update elapsed time for active timer
  useEffect(() => {
    if (!activeTimer || activeTimer.bookingId !== booking?.id) {
      setElapsedTime(0);
      return;
    }

    const updateTime = () => {
      setElapsedTime(getElapsedSeconds());
    };

    updateTime();
    const interval = setInterval(updateTime, 1000);

    return () => clearInterval(interval);
  }, [activeTimer, booking?.id, getElapsedSeconds]);

  useEffect(() => {
    const fetchData = async () => {
      if (!user?.id || !selectedBusiness?.id || !supabase || !booking) return;

      setIsLoadingData(true);
      try {
        // Fetch current resource
        console.log('🔍 BookingQuickInfoModal: Fetching resource for user:', user.id, 'business:', selectedBusiness.id);
        
        const { data: resourceData, error: resourceError } = await supabase
          .from('resources')
          .select('id, first_name, last_name, avatar_url, auth_user_id')
          .eq('auth_user_id', user.id)
          .eq('business_id', selectedBusiness.id)
          .single();

        console.log('👤 BookingQuickInfoModal: Resource query result:', { resourceData, resourceError });
        
        if (!resourceError && resourceData) {
          setCurrentResource(resourceData);
          console.log('✅ BookingQuickInfoModal: Current resource set:', resourceData.id);
        } else {
          console.error('❌ BookingQuickInfoModal: Failed to fetch resource:', resourceError);
        }

        // Fetch assigned resources
        const { data: assignedResourcesData, error: assignedError } = await supabase
          .from('booking_resources')
          .select(`
            resource_id,
            resources (
              id,
              first_name,
              last_name,
              avatar_url
            )
          `)
          .eq('booking_id', booking.id);

        if (!assignedError && assignedResourcesData) {
          const resources = assignedResourcesData
            .map(br => br.resources)
            .filter(Boolean) as AssignedResource[];
          setAssignedResources(resources);
        }

                 // Check for workflow steps in booking services (JSONB approach)
         if (booking.services && booking.services.length > 0) {
           const allWorkflowSteps: WorkflowStep[] = [];
           let hasAnyWorkflow = false;

           booking.services.forEach((service: any) => {
             if (service.workflow_steps && Array.isArray(service.workflow_steps)) {
               hasAnyWorkflow = true;
               const serviceSteps = service.workflow_steps.map((step: any, index: number) => ({
                 id: step.id || `step_${index}`,
                 name: step.title || step.name,
                 is_completed: step.status === 'completed',
                 order_index: step.order_index || index,
                 assigned_resources: step.assigned_resources || []
               }));
               allWorkflowSteps.push(...serviceSteps);
             }
           });

           setHasWorkflow(hasAnyWorkflow);
           setWorkflowSteps(allWorkflowSteps);
           console.log('🔍 BookingQuickInfoModal: Found workflow steps in services:', {
             hasWorkflow: hasAnyWorkflow,
             totalSteps: allWorkflowSteps.length,
             steps: allWorkflowSteps
           });
         }
      } catch (error) {
        console.error('Error fetching data:', error);
      } finally {
        setIsLoadingData(false);
      }
    };

    if (isOpen && booking) {
      fetchData();
    }
  }, [isOpen, user?.id, selectedBusiness?.id, booking]);

  if (!isOpen || !booking) return null;

  // Check if this booking has an active timer
  const isTimerActive = activeTimer?.bookingId === booking.id;
  
  // Check if another booking has an active timer
  const hasOtherActiveTimer = activeTimer && activeTimer.bookingId !== booking.id;

  // Format time as HH:MM:SS
  const formatTime = (seconds: number) => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    
    return `${hours.toString().padStart(2, '0')}:${minutes
      .toString()
      .padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  const handleTimerToggle = async () => {
    if (!currentResource || !selectedBusiness) return;
    
    setTimerLoading(true);
    try {
      if (isTimerActive) {
        // Stop the timer
        await stopTimer();
      } else {
        // Start the timer
        await startTimer(
          booking.id,
          booking.title || `Booking #${booking.booking_number}`,
          booking.booking_number || '',
          selectedBusiness.id,
          currentResource.id
        );
      }
    } catch (error) {
      console.error('Error toggling timer:', error);
    } finally {
      setTimerLoading(false);
    }
  };

  const handleCompleteStep = async (step: WorkflowStep) => {
    if (!currentResource || !booking) return;
    
    setLoading(true);
    try {
      console.log('🎯 Completing step:', step.name);
      
      // Check if booking is in 'in_progress' status
      if (booking.status !== 'in_progress') {
        console.error('Workflow steps can only be completed when the booking is in progress');
        return;
      }

      // Find the service that contains this workflow step
      const serviceWithStep = booking.services?.find((service: any) => 
        service.workflow_steps?.some((ws: any) => ws.id === step.id || ws.title === step.name)
      );

      if (!serviceWithStep) {
        console.error('Could not find service containing this workflow step');
        return;
      }

      // Update the step status in the JSONB column
      const updatedWorkflowSteps = serviceWithStep.workflow_steps.map((ws: any) => 
        (ws.id === step.id || ws.title === step.name)
          ? { ...ws, status: 'completed', completed_at: new Date().toISOString() }
          : ws
      );

      // Update the booking_services table with the new workflow_steps data
      const { error } = await supabase
        .from('booking_services')
        .update({ workflow_steps: updatedWorkflowSteps })
        .eq('id', serviceWithStep.id);

      if (error) throw error;

      // Update local state
      const updatedSteps = workflowSteps.map(s => 
        s.id === step.id 
          ? { ...s, is_completed: true }
          : s
      );
      setWorkflowSteps(updatedSteps);
      
      console.log('✅ Step completed successfully');
    } catch (error) {
      console.error('❌ Error completing step:', error);
    } finally {
      setLoading(false);
    }
  };

  const completedSteps = workflowSteps.filter(step => step.is_completed).length;
  const totalSteps = workflowSteps.length;

  const modalContent = (
    <div 
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999] p-4"
      onClick={onClose}
    >
      <div 
        className="bg-white rounded-lg shadow-xl max-w-md w-full max-h-[90vh] overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        {isLoadingData ? (
          // Loading state
          <div className="flex items-center justify-center p-8">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600"></div>
          </div>
        ) : (
          <>
            {/* Header with assigned resources */}
            <div className="flex items-center justify-between p-4 border-b bg-gray-50">
          <div className="flex items-center space-x-3">
            <div className="flex -space-x-2">
              {assignedResources.length > 0 ? (
                assignedResources.map((resource, index) => (
                  <div
                    key={resource.id}
                    className="w-8 h-8 rounded-full border-2 border-white bg-gray-300 flex items-center justify-center text-xs font-medium text-gray-700"
                    title={`${resource.first_name} ${resource.last_name}`}
                  >
                    {resource.avatar_url ? (
                      <img
                        src={resource.avatar_url}
                        alt={`${resource.first_name} ${resource.last_name}`}
                        className="w-full h-full rounded-full object-cover"
                      />
                    ) : (
                      <User className="w-4 h-4" />
                    )}
                  </div>
                ))
              ) : (
                <div className="w-8 h-8 rounded-full border-2 border-white bg-gray-300 flex items-center justify-center">
                  <User className="w-4 h-4 text-gray-600" />
                </div>
              )}
            </div>
            <div>
              <h2 className="text-lg font-semibold text-gray-900">
                {booking.title || `Booking #${booking.booking_number}`}
              </h2>
              <p className="text-sm text-gray-500">
                {booking.customer?.first_name} {booking.customer?.last_name}
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

                 {/* Content */}
         <div className="p-4 space-y-4 overflow-y-auto max-h-[60vh]">
           {/* Services - only show if no workflow is assigned */}
           {!hasWorkflow && booking.services && booking.services.length > 0 && (
             <div className="space-y-2">
               <h3 className="text-sm font-medium text-gray-700 flex items-center">
                 <Wrench className="h-4 w-4 mr-2" />
                 Services
               </h3>
               <div className="bg-gray-50 rounded-md p-3">
                 <ul className="text-sm text-gray-600 space-y-1">
                   {booking.services.map((service, index) => (
                     <li key={index} className="flex items-center">
                       <Car className="h-3 w-3 mr-2 text-gray-400" />
                       {service.service_name || service.name}
                     </li>
                   ))}
                 </ul>
               </div>
             </div>
           )}

                     {/* Workflow Steps */}
           {hasWorkflow && workflowSteps.length > 0 && (
             <div className="space-y-2">
               <h3 className="text-sm font-medium text-gray-700">
                 Workflow Steps ({completedSteps}/{totalSteps})
               </h3>
               <div className="bg-gray-50 rounded-md p-3">
                 <div className="space-y-2">
                   {workflowSteps.map((step) => (
                     <div key={step.id} className="flex items-center space-x-2">
                       {step.is_completed ? (
                         <CheckCircle className="h-4 w-4 text-green-500" />
                       ) : (
                         <Circle className="h-4 w-4 text-gray-400" />
                       )}
                                             <div className="flex-1 min-w-0">
                        <span className={`text-sm ${step.is_completed ? 'text-green-700 line-through' : 'text-gray-700'}`}>
                          {step.name}
                        </span>
                        
                        {/* Assigned Resources */}
                        {step.assigned_resources && step.assigned_resources.length > 0 && (
                          <div className="flex items-center gap-1 mt-1">
                            {step.assigned_resources.map((resource: any, resIndex: number) => (
                              <div key={resIndex} className="flex items-center gap-1">
                                <div className="w-5 h-5 rounded bg-gray-300 overflow-hidden border border-white">
                                  {resource.avatar_url ? (
                                    <img
                                      src={resource.avatar_url}
                                      alt={resource.name}
                                      className="w-full h-full object-cover"
                                    />
                                  ) : (
                                    <div className="w-full h-full flex items-center justify-center bg-purple-500 text-white text-xs font-bold">
                                      {resource.name.split(' ').map((n: string) => n[0]).join('')}
                                    </div>
                                  )}
                                </div>
                              </div>
                            ))}
                          </div>
                        )}

                        {/* Complete Button - only show if current resource is assigned and step is not completed */}
                        {!step.is_completed && 
                         currentResource && 
                         step.assigned_resources?.some((resource: any) => 
                           resource.id === currentResource.id || 
                           resource.resource_id === currentResource.id ||
                           resource.name === `${currentResource.first_name} ${currentResource.last_name}`
                         ) && (
                          <div className="mt-2">
                            <button 
                              className="px-3 py-1.5 bg-green-600 text-white text-xs font-medium rounded-md hover:bg-green-700 transition-colors"
                              onClick={() => handleCompleteStep(step)}
                              disabled={loading}
                            >
                              {loading ? 'Completing...' : 'Complete'}
                            </button>
                          </div>
                        )}
                      </div>
                     </div>
                   ))}
                 </div>
               </div>
             </div>
           )}
           
           

          {/* Booking Details */}
          <div className="space-y-2">
            <h3 className="text-sm font-medium text-gray-700">Details</h3>
            <div className="bg-gray-50 rounded-md p-3 space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-gray-600">Status:</span>
                <span className={`font-medium ${
                  booking.status === 'completed' ? 'text-green-600' :
                  booking.status === 'in_progress' ? 'text-yellow-600' :
                  booking.status === 'cancelled' ? 'text-red-600' :
                  booking.status === 'scheduled' ? 'text-blue-600' :
                  'text-gray-600'
                }`}>
                  {booking.status.replace('_', ' ').toUpperCase()}
                </span>
              </div>
              {booking.scheduled_at && (
                <div className="flex justify-between">
                  <span className="text-gray-600">Scheduled:</span>
                  <span className="text-gray-700">
                    {new Date(booking.scheduled_at).toLocaleDateString()}
                  </span>
                </div>
              )}
              {booking.total && (
                <div className="flex justify-between">
                  <span className="text-gray-600">Total:</span>
                  <span className="font-medium text-gray-700">
                    ${parseFloat(booking.total).toFixed(2)}
                  </span>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Timer Button - Only show for in_progress bookings */}
        {booking.status === 'in_progress' && (
          <div className="p-4 border-t bg-gray-50">
            {/* Warning about other active timer */}
            {hasOtherActiveTimer && !isTimerActive && (
              <div className="mb-3 p-2 bg-yellow-50 border border-yellow-200 rounded-md">
                <div className="flex items-center text-sm text-yellow-800">
                  <Clock className="h-4 w-4 mr-2 flex-shrink-0" />
                  <span>
                    Active timer on <strong>#{activeTimer.bookingNumber}</strong> will be stopped
                  </span>
                </div>
              </div>
            )}
            
            <div className="flex items-center space-x-3">
              <button
                onClick={handleTimerToggle}
                disabled={timerLoading || !currentResource || isLoadingData}
                className={`flex-1 px-4 py-3 rounded-md font-medium
                         transition-colors flex items-center justify-center space-x-2
                         disabled:opacity-50 disabled:cursor-not-allowed
                         ${isTimerActive 
                           ? 'bg-red-600 text-white hover:bg-red-700' 
                           : 'bg-purple-600 text-white hover:bg-purple-700'}`}
              >
                {timerLoading ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                    <span>{isTimerActive ? 'Stopping...' : 'Starting...'}</span>
                  </>
                ) : (
                  <>
                    {isTimerActive ? (
                      <>
                        <Square className="h-4 w-4" />
                        <span>Stop Timer</span>
                      </>
                                    ) : (
                    <>
                      <Play className="h-4 w-4" />
                      <span>Start Timer</span>
                    </>
                  )}
                  </>
                )}
              </button>
              
              {/* Show elapsed time when timer is active */}
              {isTimerActive && !timerLoading && (
                <div className="bg-gray-800 text-white px-4 py-3 rounded-md min-w-[100px] text-center">
                  <div className="text-lg font-mono font-bold">
                    {formatTime(elapsedTime)}
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

          </>
        )}
      </div>
    </div>
  );

  return createPortal(modalContent, document.body);
}
